import { useNodeProps } from '@/node-editor/hooks';
import { INode, INodeProps } from '@/types/node';
import { IMenuItem } from '@/types/menu';
import { Box, Container, Icon, IconButton } from '@mui/material';
import { useTheme, Theme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import React, { useContext } from 'react';
import { IContent } from './contract';
import { AuthContext, ConferenceContext, MenuContext } from '@/store';
import { TopMenuItem } from '@/components/topmenuitem';

interface INodeExtended extends Omit<INode, 'content'> {
  content: IContent;
}

interface IProps extends Omit<INodeProps, 'node'> {
  node: INodeExtended;
}

const useStyles = (
  theme: Theme,
  containerWidth: string | undefined | boolean,
  override: any
) => ({
  container: {
    display: 'flex !important',
    width: '100% !important',
    alignItems: 'center !important',
    paddingLeft: `${theme.spacing(1)} !important`,
    paddingRight: `${theme.spacing(1)} !important`,
    paddingTop: '5px !important',
    paddingBottom: '5px !important',
    minHeight: '54px !important',
    maxWidth:
      containerWidth === 'full' || !containerWidth
        ? `none !important`
        : `${
            theme.breakpoints.values[
              containerWidth as keyof typeof theme.breakpoints.values
            ]
          }px !important`,
    ...override?.root,
  },
  left: {
    flexGrow: '1 !important',
  },
  right: {
    textAlign: 'right !important',
  },
});

export const Render = ({
  node: {
    id,
    override,
    content: { containerWidth },
  },
  conferenceId,
}: IProps) => {
  const theme = useTheme();
  const classes = useStyles(theme, containerWidth, override);
  const { setMenuOpen } = useContext(MenuContext);
  const {
    conferenceState: { conferenceDetail },
  } = useContext(ConferenceContext);
  const { authState } = useContext(AuthContext); // Hämta Context
  const smallScreen = useMediaQuery(theme.breakpoints.down('sm'), {
    noSsr: true,
  });
  const items: IMenuItem[] = conferenceDetail.menu_items.filter(
    (m: IMenuItem) =>
      m.type === 'site' && !(m.onlypublic && authState.userLoggedIn)
  );

  const left = items.filter((m) => m.position === 'start');
  const right = items.filter((m) => m.position === 'end');
  const elevated = items.filter((m) => m.design === 'elevated');
  const leftElevated = elevated.filter((m) => m.position === 'start');
  const rightElevated = elevated.filter((m) => m.position === 'end');
  const unElevated = items.filter((m) => m.design !== 'elevated');

  /**
   * Menu rendering Logic:
   * - IF no items OR (mobile AND logged in), hide everything
   * - IF large, show full menu
   * - IF mobile AND not logged in AND 1 left menu AND 1 unElevated, show left menu item (Eg. Home AND Register)
   * - IF mobile AND not logged, show Elevated
   * - IF mobile AND not logged AND 2+ unElevated, show hamburger
   */

  return items.length === 0 ||
    (smallScreen && authState.userLoggedIn) ? null : (
    <Container {...useNodeProps(id)} sx={classes.container}>
      <Box sx={classes.left}>
        {smallScreen && unElevated.length > 1 && (
          <IconButton onClick={() => setMenuOpen(true)} size="small">
            <Icon>menu</Icon>
          </IconButton>
        )}

        {!smallScreen ? (
          left.map((m) => (
            <TopMenuItem data={m} key={m.id} conferenceId={conferenceId} />
          ))
        ) : left.length === 1 && unElevated.length === 1 ? (
          <TopMenuItem data={left[0]} conferenceId={conferenceId} />
        ) : (
          leftElevated.map((m) => (
            <TopMenuItem data={m} key={m.id} conferenceId={conferenceId} />
          ))
        )}
      </Box>
      <Box sx={classes.right}>
        {!smallScreen
          ? right.map((m) => (
              <TopMenuItem data={m} key={m.id} conferenceId={conferenceId} />
            ))
          : rightElevated.map((m) => (
              <TopMenuItem data={m} key={m.id} conferenceId={conferenceId} />
            ))}
      </Box>
    </Container>
  );
};
